import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, Eye, Download, ArrowLeft, Edit2, Plus, Trash2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

/**
 * Interactive Agreement Template Editor
 * WYSIWYG editor that matches the PDF mockup exactly
 * Click any field to edit directly on the template
 */
export default function AgreementTemplateEditor() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(getDefaultTemplate());
  const [editingField, setEditingField] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('page1'); // page1, page2, legal, ach, crof, implChecklist

  useEffect(() => {
    loadTemplate();
  }, []);

  const loadTemplate = async () => {
    try {
      setLoading(true);
      console.log('Loading template from backend...');
      const response = await api.get('/agreements/template-config');
      console.log('Template response:', response.data);
      
      if (response.data && response.data.config) {
        // Merge backend config with default template to ensure all fields exist
        // This prevents undefined errors if backend is missing any fields
        const defaultTemplate = getDefaultTemplate();
        const backendImpl = response.data.config.implementationChecklist || {};
        const mergedConfig = {
          ...defaultTemplate,
          ...response.data.config,
          // Deep merge nested objects
          header: { ...defaultTemplate.header, ...response.data.config.header },
          principal: { ...defaultTemplate.principal, ...response.data.config.principal },
          grant: { ...defaultTemplate.grant, ...response.data.config.grant },
          endUser: { ...defaultTemplate.endUser, ...response.data.config.endUser },
          scheduleA: { ...defaultTemplate.scheduleA, ...response.data.config.scheduleA },
          termsAndConditions: { ...defaultTemplate.termsAndConditions, ...response.data.config.termsAndConditions },
          stipulations: { ...defaultTemplate.stipulations, ...response.data.config.stipulations },
          legal: { ...defaultTemplate.legal, ...response.data.config.legal },
          footer: { ...defaultTemplate.footer, ...response.data.config.footer },
          // DEEP MERGE: implementationChecklist requires merging nested sections and bulletFormatting
          // so newly-added keys (e.g., trainingOnboarding) are not erased by older saved configs
          implementationChecklist: {
            ...defaultTemplate.implementationChecklist,
            ...backendImpl,
            sections: {
              ...defaultTemplate.implementationChecklist.sections,
              ...(backendImpl.sections || {})
            },
            bulletFormatting: {
              ...defaultTemplate.implementationChecklist.bulletFormatting,
              ...(backendImpl.bulletFormatting || {})
            },
            // Preserve other array fields from backend if present
            specialRequestsPrompts: backendImpl.specialRequestsPrompts || defaultTemplate.implementationChecklist.specialRequestsPrompts,
            userAccountRoles: backendImpl.userAccountRoles || defaultTemplate.implementationChecklist.userAccountRoles,
            depositFrequencyOptions: backendImpl.depositFrequencyOptions || defaultTemplate.implementationChecklist.depositFrequencyOptions
          }
        };
        setConfig(mergedConfig);
        console.log('Template loaded successfully');
      } else {
        console.log('No saved template found, using default');
        setConfig(getDefaultTemplate());
      }
    } catch (error) {
      console.error('Error loading template:', error);
      console.log('Loading default template due to error');
      setConfig(getDefaultTemplate());
    } finally {
      setLoading(false);
    }
  };

  const [exporting, setExporting] = useState(false);

  const handleExportPdf = async () => {
    try {
      setExporting(true);
      toast.info('Generating template PDF preview...');
      const response = await api.get('/agreements/template-config/export-pdf', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `GW_SaaS_Template_Preview_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template PDF downloaded');
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export template as PDF');
    } finally {
      setExporting(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      console.log('Saving template config...', config);
      const response = await api.post('/agreements/template-config', config);
      console.log('Save response:', response.data);
      toast.success('Template saved successfully');
      // Reload template to confirm save
      await loadTemplate();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (section, fieldKey, value) => {
    setConfig(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [fieldKey]: value
      }
    }));
  };

  const updateTableColumn = (columnId, updates) => {
    setConfig(prev => ({
      ...prev,
      scheduleA: {
        ...prev.scheduleA,
        columns: prev.scheduleA.columns.map(col =>
          col.id === columnId ? { ...col, ...updates } : col
        )
      }
    }));
  };

  const addTableColumn = () => {
    const newColumn = {
      id: `col_${Date.now()}`,
      label: 'New Column',
      dbField: '',
      type: 'text',
      visible: true
    };
    setConfig(prev => ({
      ...prev,
      scheduleA: {
        ...prev.scheduleA,
        columns: [...prev.scheduleA.columns, newColumn]
      }
    }));
  };

  const deleteTableColumn = (columnId) => {
    setConfig(prev => ({
      ...prev,
      scheduleA: {
        ...prev.scheduleA,
        columns: prev.scheduleA.columns.filter(col => col.id !== columnId)
      }
    }));
  };

  // Show loading state while fetching template
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 py-8">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <div className="animate-pulse">
              <div className="text-lg text-slate-600">Loading template...</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 py-8">
      <div className="max-w-[1200px] mx-auto px-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/agreements')}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Agreement Template Editor</h1>
              <p className="text-sm text-slate-600">Edit fields directly on the template - changes save automatically</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/agreements')}
              className="px-4 py-2 text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg font-medium"
            >
              Back
            </button>
            <button
              onClick={handleExportPdf}
              disabled={exporting || saving}
              data-testid="export-template-pdf-btn"
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-800 text-white rounded-lg font-medium disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              {exporting ? 'Exporting...' : 'Export PDF'}
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Template'}
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-t-lg shadow-sm border-b border-slate-200">
          <div className="flex gap-1 p-2">
            <button
              onClick={() => setActiveTab('page1')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'page1'
                  ? 'bg-indigo-100 text-indigo-700'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              Page 1 - Agreement Face
            </button>
            <button
              onClick={() => setActiveTab('page2')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'page2'
                  ? 'bg-indigo-100 text-indigo-700'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              Page 2 - Stipulations
            </button>
            <button
              onClick={() => setActiveTab('legal')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'legal'
                  ? 'bg-indigo-100 text-indigo-700'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              Pages 3-4 - Legal Terms
            </button>
            <button
              onClick={() => setActiveTab('ach')}
              data-testid="tpl-tab-ach"
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'ach'
                  ? 'bg-indigo-100 text-indigo-700'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              ACH Agreement
            </button>
            <button
              onClick={() => setActiveTab('crof')}
              data-testid="tpl-tab-crof"
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'crof'
                  ? 'bg-indigo-100 text-indigo-700'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              Card Reader Order Form
            </button>
            <button
              onClick={() => setActiveTab('implChecklist')}
              data-testid="tpl-tab-impl-checklist"
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'implChecklist'
                  ? 'bg-teal-100 text-teal-700'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              Implementation Checklist
            </button>
          </div>
        </div>

        {/* Template Content */}
        <div className="bg-white shadow-lg rounded-b-lg">
          {/* Page 1 - Agreement Face */}
          {activeTab === 'page1' && (
            <Page1Editor
              config={config}
              updateField={updateField}
              editingField={editingField}
              setEditingField={setEditingField}
              updateTableColumn={updateTableColumn}
              addTableColumn={addTableColumn}
              deleteTableColumn={deleteTableColumn}
            />
          )}

          {/* Page 2 - Stipulations */}
          {activeTab === 'page2' && (
            <Page2Editor
              config={config}
              updateField={updateField}
              editingField={editingField}
              setEditingField={setEditingField}
            />
          )}

          {/* Legal Terms */}
          {activeTab === 'legal' && (
            <LegalTermsEditor
              config={config}
              updateField={updateField}
            />
          )}

          {/* ACH Agreement */}
          {activeTab === 'ach' && (
            <AchAgreementEditor
              config={config}
              updateField={updateField}
            />
          )}

          {/* Card Reader Order Form */}
          {activeTab === 'crof' && (
            <CrofAgreementEditor
              config={config}
              updateField={updateField}
              setConfig={setConfig}
            />
          )}

          {activeTab === 'implChecklist' && (
            <ImplChecklistEditor
              config={config}
              updateField={updateField}
              setConfig={setConfig}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// Page 1 - Agreement Face
function Page1Editor({ config, updateField, editingField, setEditingField, updateTableColumn, addTableColumn, deleteTableColumn }) {
  return (
    <div className="p-8 space-y-6" style={{ fontFamily: 'Arial, sans-serif' }}>
      {/* Header */}
      <div className="border-b-2 border-slate-900 pb-4">
        <EditableField
          value={config.header.logo}
          onSave={(val) => updateField('header', 'logo', val)}
          className="text-2xl font-bold text-indigo-900 mb-2"
          placeholder="GOVERNMENT WINDOW"
          isEditing={editingField === 'header.logo'}
          onEdit={() => setEditingField('header.logo')}
          onClose={() => setEditingField(null)}
        />
        <EditableField
          value={config.header.title}
          onSave={(val) => updateField('header', 'title', val)}
          className="text-xl font-bold text-center"
          placeholder="No Term, Non-Exclusive SaaS Agreement"
          isEditing={editingField === 'header.title'}
          onEdit={() => setEditingField('header.title')}
          onClose={() => setEditingField(null)}
        />
      </div>

      {/* Principal Information */}
      <div>
        <p className="font-semibold mb-2">Principal:</p>
        <EditableField
          value={config.principal.name}
          onSave={(val) => updateField('principal', 'name', val)}
          className="font-medium"
          placeholder="Government Window, LLC"
          isEditing={editingField === 'principal.name'}
          onEdit={() => setEditingField('principal.name')}
          onClose={() => setEditingField(null)}
        />
        <EditableField
          value={config.principal.address}
          onSave={(val) => updateField('principal', 'address', val)}
          className="text-sm text-slate-600"
          placeholder="125 Townpark Drive NW. Suite 500, Kennesaw GA 30144"
          isEditing={editingField === 'principal.address'}
          onEdit={() => setEditingField('principal.address')}
          onClose={() => setEditingField(null)}
          multiline
        />
      </div>

      {/* Grant Statement */}
      <div className="bg-slate-50 p-4 rounded border border-slate-200">
        <EditableField
          value={config.grant.statement}
          onSave={(val) => updateField('grant', 'statement', val)}
          className="text-sm leading-relaxed"
          placeholder="This agreement sets out the terms..."
          isEditing={editingField === 'grant.statement'}
          onEdit={() => setEditingField('grant.statement')}
          onClose={() => setEditingField(null)}
          multiline
        />
      </div>

      {/* End-User Fields */}
      <div className="grid grid-cols-2 gap-4 bg-blue-50 p-4 rounded border-2 border-blue-200">
        <div>
          <label className="text-xs font-semibold text-slate-700 block mb-1">End-User (Licensee):</label>
          <div className="bg-white p-2 rounded border border-blue-300 text-sm italic text-slate-500">
            {config.endUser?.licensee?.placeholder || 'Will pull from proposal.client_name'}
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-700 block mb-1">Agency Name:</label>
          <div className="bg-white p-2 rounded border border-blue-300 text-sm italic text-slate-500">
            {config.endUser?.agency?.placeholder || 'Will pull from proposal.agency_name'}
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-700 block mb-1">Contact Name:</label>
          <div className="bg-white p-2 rounded border border-blue-300 text-sm italic text-slate-500">
            {config.endUser?.contact?.placeholder || 'Will pull from proposal.contact_name'}
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-700 block mb-1">Contact Phone:</label>
          <div className="bg-white p-2 rounded border border-blue-300 text-sm italic text-slate-500">
            {config.endUser?.phone?.placeholder || 'Will pull from proposal.contact_phone'}
          </div>
        </div>
        <div className="col-span-2">
          <label className="text-xs font-semibold text-slate-700 block mb-1">Address:</label>
          <div className="bg-white p-2 rounded border border-blue-300 text-sm italic text-slate-500">
            {config.endUser?.address?.placeholder || 'Will pull from proposal.client_address'}
          </div>
        </div>
      </div>

      {/* Schedule A */}
      <div>
        <h3 className="font-bold text-lg mb-2">Schedule "A" - Licensed Items</h3>
        <EditableField
          value={config.scheduleA.intro}
          onSave={(val) => updateField('scheduleA', 'intro', val)}
          className="text-xs text-slate-600 mb-2"
          placeholder="Schedule A lists the service fees..."
          isEditing={editingField === 'scheduleA.intro'}
          onEdit={() => setEditingField('scheduleA.intro')}
          onClose={() => setEditingField(null)}
          multiline
        />
        <EditableField
          value={config.scheduleA.functionInstruction}
          onSave={(val) => updateField('scheduleA', 'functionInstruction', val)}
          className="text-xs text-slate-600 mb-4 italic"
          placeholder="In the Function column, check the box(es) for the Module(s) you will be launching. [W]=Webpay, [P]=Point of Sale, [I]=IVR"
          isEditing={editingField === 'scheduleA.functionInstruction'}
          onEdit={() => setEditingField('scheduleA.functionInstruction')}
          onClose={() => setEditingField(null)}
          multiline
        />

        {/* Table */}
        <div className="border-2 border-slate-900 rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-white">
              <tr>
                {config.scheduleA.columns.filter(col => col.visible).map((column) => (
                  <th key={column.id} className="px-3 py-2 text-left text-xs font-bold relative group">
                    <div className="flex items-center justify-between">
                      <EditableField
                        value={column.label}
                        onSave={(val) => updateTableColumn(column.id, { label: val })}
                        className="text-white font-bold"
                        placeholder={column.label}
                        isEditing={editingField === `column.${column.id}`}
                        onEdit={() => setEditingField(`column.${column.id}`)}
                        onClose={() => setEditingField(null)}
                        inline
                      />
                      <button
                        onClick={() => deleteTableColumn(column.id)}
                        className="opacity-0 group-hover:opacity-100 ml-2 p-1 hover:bg-red-600 rounded transition-opacity"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                    <div className="text-xs font-normal text-slate-300 mt-1">
                      DB: {column.dbField || 'static'}
                    </div>
                  </th>
                ))}
                <th className="px-3 py-2 w-10">
                  <button
                    onClick={addTableColumn}
                    className="p-1 hover:bg-slate-700 rounded"
                    title="Add Column"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {(config.scheduleA?.modules || []).map((module, idx) => (
                <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                  <td className="border-t border-slate-300 px-3 py-2 font-medium">{module}</td>
                  <td className="border-t border-slate-300 px-3 py-2">
                    <div className="flex items-center justify-center gap-3">
                      <label className="flex items-center gap-1 text-xs">
                        <input type="checkbox" className="rounded" />
                        <span>W</span>
                      </label>
                      <label className="flex items-center gap-1 text-xs">
                        <input type="checkbox" className="rounded" />
                        <span>P</span>
                      </label>
                      <label className="flex items-center gap-1 text-xs">
                        <input type="checkbox" className="rounded" />
                        <span>I</span>
                      </label>
                    </div>
                  </td>
                  {config.scheduleA.columns.filter(col => col.visible).slice(2).map((column) => (
                    <td key={column.id} className="border-t border-slate-300 px-3 py-2 text-center text-slate-400">
                      {column.type === 'percentage' ? '%' : 
                       column.type === 'currency' ? '$' : 
                       column.type === 'number' ? '#' :
                       column.id === 'reader_type' ? 'Dynamag' :
                       column.id === 'reader_qty' ? '5' :
                       '—'}
                    </td>
                  ))}
                  <td className="border-t border-slate-300"></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <button
          onClick={addTableColumn}
          className="mt-2 text-xs text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1"
        >
          <Plus className="w-3 h-3" />
          Add Column to Schedule A
        </button>

        <div className="mt-4 text-xs text-slate-600 bg-blue-50 p-3 rounded border border-blue-200">
          <p className="font-semibold mb-1">📌 Note:</p>
          <p>Service modules (Taxes, DMV, Court, etc.) will be pulled from the Service Manager and available services in your system. The list shown here is for template preview only.</p>
        </div>
      </div>

      {/* Terms and Conditions */}
      <div className="border-t-2 border-slate-300 pt-6">
        <h3 className="font-bold text-lg mb-3">Terms and Conditions</h3>
        <EditableField
          value={config.termsAndConditions.content}
          onSave={(val) => updateField('termsAndConditions', 'content', val)}
          className="text-sm leading-relaxed whitespace-pre-wrap bg-slate-50 p-4 rounded border border-slate-200"
          placeholder="Add your terms and conditions text here..."
          isEditing={editingField === 'termsAndConditions.content'}
          onEdit={() => setEditingField('termsAndConditions.content')}
          onClose={() => setEditingField(null)}
          multiline
        />
      </div>

      {/* Footer */}
      <div className="text-xs text-center text-slate-500 border-t pt-4">
        <EditableField
          value={config.footer.copyright}
          onSave={(val) => updateField('footer', 'copyright', val)}
          className="text-xs text-center"
          placeholder="© 2025"
          isEditing={editingField === 'footer.copyright'}
          onEdit={() => setEditingField('footer.copyright')}
          onClose={() => setEditingField(null)}
          inline
        />
      </div>
    </div>
  );
}

// Page 2 - Stipulations
function Page2Editor({ config, updateField, editingField, setEditingField }) {
  return (
    <div className="p-8 space-y-6" style={{ fontFamily: 'Arial, sans-serif' }}>
      <h2 className="text-xl font-bold text-center border-b-2 border-slate-900 pb-2">
        Non-Exclusive SaaS Agreement - Page 2
      </h2>

      {/* Stipulations Section */}
      <div>
        <h3 className="font-bold mb-2">Stipulations & Notes: (please define:)</h3>
        <EditableField
          value={config.stipulations.costAllocation}
          onSave={(val) => updateField('stipulations', 'costAllocation', val)}
          className="text-sm leading-relaxed bg-slate-50 p-4 rounded border border-slate-200"
          placeholder="Cost allocation fees based on..."
          isEditing={editingField === 'stipulations.costAllocation'}
          onEdit={() => setEditingField('stipulations.costAllocation')}
          onClose={() => setEditingField(null)}
          multiline
        />
        <div className="mt-4 bg-yellow-50 p-4 rounded border-2 border-yellow-300">
          <p className="text-xs font-semibold text-yellow-900 mb-2">Custom Notes (from database):</p>
          <p className="text-sm italic text-yellow-800">This section will pull from agreement.notes</p>
        </div>
      </div>

      {/* Signature Blocks */}
      <div className="grid grid-cols-2 gap-6 mt-8">
        <div className="border-2 border-slate-900 p-4 rounded">
          <p className="font-bold mb-4">Principal:</p>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold">Signed:</label>
              <div className="border-b-2 border-slate-900 h-8"></div>
            </div>
            <div>
              <label className="text-xs font-semibold">Name:</label>
              <div className="border-b-2 border-slate-900 h-8"></div>
            </div>
            <div>
              <label className="text-xs font-semibold">Title:</label>
              <div className="border-b-2 border-slate-900 h-8"></div>
            </div>
            <div>
              <label className="text-xs font-semibold">Date:</label>
              <div className="border-b-2 border-slate-900 h-8"></div>
            </div>
          </div>
        </div>

        <div className="border-2 border-slate-900 p-4 rounded">
          <p className="font-bold mb-4">End-User:</p>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold">Signed:</label>
              <div className="border-b-2 border-slate-900 h-8"></div>
            </div>
            <div>
              <label className="text-xs font-semibold">Name:</label>
              <div className="border-b-2 border-slate-900 h-8"></div>
            </div>
            <div>
              <label className="text-xs font-semibold">Title:</label>
              <div className="border-b-2 border-slate-900 h-8"></div>
            </div>
            <div>
              <label className="text-xs font-semibold">Date:</label>
              <div className="border-b-2 border-slate-900 h-8"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Legal Terms Editor
function LegalTermsEditor({ config, updateField }) {
  return (
    <div className="p-8 space-y-4" style={{ fontFamily: 'Arial, sans-serif', fontSize: '12px' }}>
      <h2 className="text-lg font-bold text-center border-b-2 border-slate-900 pb-2">
        Software as a Service Agreement - Pages 3-4
      </h2>

      <div className="bg-slate-50 p-4 rounded border border-slate-200">
        <p className="text-sm font-semibold text-slate-700 mb-2">Legal Terms Configuration:</p>
        <div className="space-y-2">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={config.legal.includeDefinitions}
              onChange={(e) => updateField('legal', 'includeDefinitions', e.target.checked)}
              className="rounded"
            />
            <span className="text-sm">Include Section 1: Definitions</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={config.legal.includeLicense}
              onChange={(e) => updateField('legal', 'includeLicense', e.target.checked)}
              className="rounded"
            />
            <span className="text-sm">Include Section 2: License Terms</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={config.legal.includeTermination}
              onChange={(e) => updateField('legal', 'includeTermination', e.target.checked)}
              className="rounded"
            />
            <span className="text-sm">Include Section 4: Termination</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={config.legal.includeWarranty}
              onChange={(e) => updateField('legal', 'includeWarranty', e.target.checked)}
              className="rounded"
            />
            <span className="text-sm">Include Section 6: Warranty</span>
          </label>
          <div className="mt-4">
            <label className="text-sm font-semibold block mb-1">Governing Law:</label>
            <input
              type="text"
              value={config.legal.governingLaw}
              onChange={(e) => updateField('legal', 'governingLaw', e.target.value)}
              className="input-field w-full"
              placeholder="State of Georgia"
            />
          </div>
        </div>
      </div>

      <div className="bg-blue-50 p-4 rounded border border-blue-200">
        <p className="text-xs text-blue-900 leading-relaxed">
          <strong>Note:</strong> The full legal text for Definitions, License, Termination, Warranty, and General clauses 
          is stored as boilerplate and will be included in the final PDF based on the checkboxes above.
        </p>
      </div>
    </div>
  );
}

// ACH Agreement Editor — admin-editable verbatim clauses
function AchAgreementEditor({ config, updateField }) {
  const ach = config.achAgreement || {};
  return (
    <div className="p-8 space-y-6" style={{ fontFamily: 'Arial, sans-serif' }}>
      <div className="border-b-2 border-slate-900 pb-4 mb-2">
        <h2 className="text-2xl font-bold text-slate-900">ACH/eCheck Processing Agreement</h2>
        <p className="text-sm text-slate-600">Edit the locked-language clauses that appear on the ACH page of every exported SaaS Agreement. Per-agreement fields (EIN, website, signatures, etc.) live on the agreement form, not here.</p>
      </div>

      <div>
        <label className="block text-sm font-bold text-slate-700 mb-1">Grant Statement</label>
        <p className="text-xs text-slate-500 mb-2">Opening line that names the Principal and grants the license.</p>
        <textarea
          data-testid="ach-grant-statement"
          value={ach.grantStatement || ''}
          onChange={(e) => updateField('achAgreement', 'grantStatement', e.target.value)}
          className="input-field w-full font-mono text-xs"
          rows={4}
        />
      </div>

      {/* ── Licensed Items Table Skeleton Preview ── */}
      <div data-testid="ach-licensed-items-preview">
        <label className="block text-sm font-bold text-slate-700 mb-1">Licensed Items Table (auto-populated per agreement)</label>
        <p className="text-xs text-slate-500 mb-2">
          This table is auto-built on every PDF export from each scenario's pricing data —
          one row per licensed Module. Columns are fixed; the data shown below is a sample preview.
        </p>
        <div className="border border-slate-300 rounded overflow-hidden bg-white">
          <table className="w-full text-xs">
            <thead className="bg-slate-100 border-b border-slate-300">
              <tr>
                <th className="text-left px-3 py-2 font-bold">Module (Service Type)</th>
                <th className="text-center px-3 py-2 font-bold">ACH Limit</th>
                <th className="text-center px-3 py-2 font-bold">ACH Fee (per txn)</th>
                <th className="text-left px-3 py-2 font-bold">Return Handler</th>
              </tr>
            </thead>
            <tbody className="text-slate-500 italic">
              <tr className="border-b border-slate-200">
                <td className="px-3 py-2">{'{ Service 1 — e.g., Child Support }'}</td>
                <td className="text-center px-3 py-2">{'{ admin-set, e.g., $10,000 / No Limit }'}</td>
                <td className="text-center px-3 py-2">$ {'{ from pricing — locked }'}</td>
                <td className="px-3 py-2">{'{ Government Window | End User }'}</td>
              </tr>
              <tr className="border-b border-slate-200 bg-slate-50">
                <td className="px-3 py-2">{'{ Service 2 — e.g., Traffic Violations }'}</td>
                <td className="text-center px-3 py-2">{'{ admin-set }'}</td>
                <td className="text-center px-3 py-2">$ {'{ from pricing — locked }'}</td>
                <td className="px-3 py-2">{'{ Government Window | End User }'}</td>
              </tr>
              <tr>
                <td className="px-3 py-2">{'... one row per Module with eCheck/ACH pricing'}</td>
                <td className="text-center px-3 py-2">—</td>
                <td className="text-center px-3 py-2">—</td>
                <td className="px-3 py-2">—</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-xs text-slate-400 mt-2 italic">
          Module names + ACH Fees are pulled from each scenario's pricing (read-only on the Agreement form).
          ACH Limit and Return Handler are editable per-agreement on the Agreement form.
        </p>
      </div>

      <div>
        <label className="block text-sm font-bold text-slate-700 mb-1">AUTHORIZATION</label>
        <p className="text-xs text-slate-500 mb-2">Verbatim NACHA authorization paragraph. Edit only with legal review.</p>
        <textarea
          data-testid="ach-authorization-text"
          value={ach.authorizationText || ''}
          onChange={(e) => updateField('achAgreement', 'authorizationText', e.target.value)}
          className="input-field w-full font-mono text-xs"
          rows={14}
        />
      </div>

      <div>
        <label className="block text-sm font-bold text-slate-700 mb-1">TERMS AND CONDITIONS</label>
        <p className="text-xs text-slate-500 mb-2">Short closing paragraph linking to the standard ACH/Check Processing T&C document.</p>
        <textarea
          data-testid="ach-terms-and-conditions-text"
          value={ach.termsAndConditionsText || ''}
          onChange={(e) => updateField('achAgreement', 'termsAndConditionsText', e.target.value)}
          className="input-field w-full font-mono text-xs"
          rows={6}
        />
      </div>

      <div className="bg-amber-50 p-4 rounded border border-amber-200">
        <p className="text-xs text-amber-900 leading-relaxed">
          <strong>Note:</strong> Changes here apply to all future PDF exports. Existing agreements continue to display the text saved at the time of their generation. HTML formatting tags (e.g. <code>&lt;a href=&quot;...&quot;&gt;</code>, <code>&lt;b&gt;</code>) are supported.
        </p>
      </div>
    </div>
  );
}

// CROF Editor — Terms of Supply (5 sections) + acknowledgement bullets + 3 cost options
function CrofAgreementEditor({ config, updateField, setConfig }) {
  const crof = config.crofAgreement || {};
  const tos = crof.termsOfSupply || [];
  const bullets = crof.acknowledgementBullets || [];
  const costOptions = crof.costOptions || [
    { number: 1, title: 'Pass-Through (Client Assumes Reader Costs)', description: 'Client assumes ${total}/year in card reader costs.' },
    { number: 2, title: 'Cost Recovery (Rate Adjustment)', description: 'Rate adjustment of +{rate}% per transaction recovers ${total} annually.' },
    { number: 3, title: 'GW Absorbs Reader Costs', description: 'Government Window absorbs ${total}/year as our value proposition — a direct cost savings to the Licensee.' },
  ];

  const updateTosSection = (idx, key, value) => {
    setConfig(prev => ({
      ...prev,
      crofAgreement: {
        ...prev.crofAgreement,
        termsOfSupply: (prev.crofAgreement?.termsOfSupply || []).map((s, i) =>
          i === idx ? { ...s, [key]: value } : s
        ),
      },
    }));
  };

  const updateBullet = (idx, value) => {
    setConfig(prev => ({
      ...prev,
      crofAgreement: {
        ...prev.crofAgreement,
        acknowledgementBullets: (prev.crofAgreement?.acknowledgementBullets || []).map((b, i) =>
          i === idx ? value : b
        ),
      },
    }));
  };

  const updateCostOption = (idx, key, value) => {
    setConfig(prev => {
      const existing = prev.crofAgreement?.costOptions || costOptions;
      return {
        ...prev,
        crofAgreement: {
          ...prev.crofAgreement,
          costOptions: existing.map((o, i) => i === idx ? { ...o, [key]: value } : o),
        },
      };
    });
  };

  return (
    <div className="p-8 space-y-6" style={{ fontFamily: 'Arial, sans-serif' }}>
      <div className="border-b-2 border-slate-900 pb-4 mb-2">
        <h2 className="text-2xl font-bold text-slate-900">Card Reader Order Form</h2>
        <p className="text-sm text-slate-600">Edit the Terms of Supply text that appears on the CROF page of every exported SaaS Agreement.</p>
      </div>

      <div>
        <h3 className="text-base font-bold text-slate-800 mb-2">Card Reader Cost Options</h3>
        <p className="text-xs text-slate-500 mb-3">
          The 3 cost-recovery options shown on every CROF page. Variables: <code className="bg-slate-100 px-1 rounded">{'{total}'}</code> (auto-filled with annual reader cost), <code className="bg-slate-100 px-1 rounded">{'{rate}'}</code> (auto-filled with computed rate-adjustment %).
        </p>
        <div className="space-y-3" data-testid="crof-cost-options-editor">
          {costOptions.map((opt, i) => (
            <div key={i} className="border-2 border-slate-200 rounded p-3 bg-slate-50" data-testid={`crof-cost-option-${i + 1}-row`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="bg-indigo-600 text-white font-bold text-xs rounded-full w-6 h-6 flex items-center justify-center">{opt.number}</span>
                <input
                  data-testid={`crof-cost-option-${i + 1}-title`}
                  value={opt.title || ''}
                  onChange={(e) => updateCostOption(i, 'title', e.target.value)}
                  className="input-field flex-1 font-bold text-sm"
                  placeholder="Option title"
                />
              </div>
              <textarea
                data-testid={`crof-cost-option-${i + 1}-description`}
                value={opt.description || ''}
                onChange={(e) => updateCostOption(i, 'description', e.target.value)}
                className="input-field w-full text-xs"
                rows={2}
                placeholder="Option description (use {total} and {rate} for auto-fill)"
              />
            </div>
          ))}
        </div>
      </div>

      {/* ── Order Details Table Skeleton (read-only preview) ── */}
      <div data-testid="crof-order-details-preview">
        <h3 className="text-base font-bold text-slate-800 mb-2">Order Details Table (auto-populated per agreement)</h3>
        <p className="text-xs text-slate-500 mb-2">
          The header block at the top of every CROF page. Filled out by the End-User on the Agreement form.
        </p>
        <div className="border border-slate-300 rounded overflow-hidden bg-white">
          <table className="w-full text-xs">
            <tbody className="text-slate-500 italic">
              {[
                ['Sales Person', '{ admin assigned to deal }'],
                ['Customer Name', '{ End-User / Licensee — auto from agreement }'],
                ['Shipping Address', '{ End-User shipping address }'],
                ['Contact Phone', '{ End-User phone }'],
                ['Contact Email', '{ End-User email }'],
                ['Additional Notes', '{ optional notes }'],
              ].map(([k, v], i) => (
                <tr key={k} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                  <td className="px-3 py-2 font-bold text-slate-700 not-italic" style={{ width: '180px' }}>{k}:</td>
                  <td className="px-3 py-2">{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Card Reader Information Table Skeleton ── */}
      <div data-testid="crof-reader-info-preview">
        <h3 className="text-base font-bold text-slate-800 mb-2">Card Reader Information Table (auto-populated per agreement)</h3>
        <p className="text-xs text-slate-500 mb-2">
          Auto-built on every PDF export from each scenario's reader pricing.
          One row per Module that has reader quantity &gt; 0. Total Annual computed from settings → Reader Types.
        </p>
        <div className="border border-slate-300 rounded overflow-hidden bg-white">
          <table className="w-full text-xs">
            <thead className="bg-slate-100 border-b border-slate-300">
              <tr>
                <th className="text-left px-3 py-2 font-bold">Module (Service)</th>
                <th className="text-center px-3 py-2 font-bold">Reader Type</th>
                <th className="text-center px-3 py-2 font-bold">Quantity</th>
                <th className="text-center px-3 py-2 font-bold">Unit Price (mo)</th>
                <th className="text-center px-3 py-2 font-bold">Annual Cost</th>
              </tr>
            </thead>
            <tbody className="text-slate-500 italic">
              <tr className="border-b border-slate-200">
                <td className="px-3 py-2">{'{ Service 1 — e.g., Property Tax }'}</td>
                <td className="text-center px-3 py-2">{'{ A35 / A920 / Dynamag }'}</td>
                <td className="text-center px-3 py-2">{'{ qty }'}</td>
                <td className="text-center px-3 py-2">{'$ { from Settings → Reader Types }'}</td>
                <td className="text-center px-3 py-2">{'$ { qty × unit × 12 }'}</td>
              </tr>
              <tr className="border-b border-slate-200 bg-slate-50">
                <td className="px-3 py-2">{'{ Service 2 — e.g., Utility Pay }'}</td>
                <td className="text-center px-3 py-2">{'{ reader type }'}</td>
                <td className="text-center px-3 py-2">{'{ qty }'}</td>
                <td className="text-center px-3 py-2">{'$ { auto }'}</td>
                <td className="text-center px-3 py-2">{'$ { auto }'}</td>
              </tr>
              <tr className="bg-indigo-50 border-t-2 border-indigo-300">
                <td className="px-3 py-2 font-bold not-italic" colSpan={4}>Total Annual</td>
                <td className="text-center px-3 py-2 font-bold not-italic">$ {'{ sum of all rows }'}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-xs text-slate-400 mt-2 italic">
          Unit prices are read from <code className="bg-slate-100 px-1 rounded">Settings → Reader Types</code>.
          To change a reader's monthly cost, edit it in Settings; the change flows to all future CROF exports.
        </p>
      </div>

      <div>
        <h3 className="text-base font-bold text-slate-800 mb-2">Terms of Supply Sections</h3>
        <div className="space-y-4">
          {tos.map((sec, i) => (
            <div key={i} className="border border-slate-200 rounded p-3 bg-slate-50">
              <input
                data-testid={`crof-tos-${i}-heading`}
                value={sec.heading || ''}
                onChange={(e) => updateTosSection(i, 'heading', e.target.value)}
                className="input-field w-full font-bold mb-2"
                placeholder="Section heading"
              />
              <textarea
                data-testid={`crof-tos-${i}-body`}
                value={sec.body || ''}
                onChange={(e) => updateTosSection(i, 'body', e.target.value)}
                className="input-field w-full text-xs"
                rows={4}
                placeholder="Section body"
              />
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-base font-bold text-slate-800 mb-2">Customer Acknowledgement Bullets</h3>
        <div className="space-y-2">
          {bullets.map((b, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="text-slate-500 font-bold">•</span>
              <input
                data-testid={`crof-ack-bullet-${i}`}
                value={b || ''}
                onChange={(e) => updateBullet(i, e.target.value)}
                className="input-field w-full text-sm"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="bg-amber-50 p-4 rounded border border-amber-200">
        <p className="text-xs text-amber-900 leading-relaxed">
          <strong>Note:</strong> Section headings and bullets follow exactly the order shown here. To preserve the visual hierarchy on PDF, keep numeric prefixes (e.g. &quot;1. Equipment Ownership...&quot;) in the heading.
        </p>
      </div>
    </div>
  );
}

// Implementation Checklist Editor
function ImplChecklistEditor({ config, updateField, setConfig }) {
  const implConfig = config.implementationChecklist || {};
  const sections = implConfig.sections || {};
  const bulletFormatting = implConfig.bulletFormatting || {};
  const specialRequestsPrompts = implConfig.specialRequestsPrompts || [];
  const userAccountRoles = implConfig.userAccountRoles || [];
  const depositFrequencyOptions = implConfig.depositFrequencyOptions || [];

  const updateSection = (sectionKey, updates) => {
    setConfig(prev => ({
      ...prev,
      implementationChecklist: {
        ...prev.implementationChecklist,
        sections: {
          ...prev.implementationChecklist?.sections,
          [sectionKey]: {
            ...prev.implementationChecklist?.sections?.[sectionKey],
            ...updates
          }
        }
      }
    }));
  };

  const updateBulletFormat = (fieldKey, enabled) => {
    setConfig(prev => ({
      ...prev,
      implementationChecklist: {
        ...prev.implementationChecklist,
        bulletFormatting: {
          ...prev.implementationChecklist?.bulletFormatting,
          [fieldKey]: {
            ...prev.implementationChecklist?.bulletFormatting?.[fieldKey],
            enabled
          }
        }
      }
    }));
  };

  const updatePrompts = (newPrompts) => {
    setConfig(prev => ({
      ...prev,
      implementationChecklist: {
        ...prev.implementationChecklist,
        specialRequestsPrompts: newPrompts
      }
    }));
  };

  const updateRoles = (newRoles) => {
    setConfig(prev => ({
      ...prev,
      implementationChecklist: {
        ...prev.implementationChecklist,
        userAccountRoles: newRoles
      }
    }));
  };

  const updateDepositOptions = (newOptions) => {
    setConfig(prev => ({
      ...prev,
      implementationChecklist: {
        ...prev.implementationChecklist,
        depositFrequencyOptions: newOptions
      }
    }));
  };

  return (
    <div className="p-8 space-y-8" data-testid="impl-checklist-editor">
      {/* Header */}
      <div className="border-b-2 border-teal-600 pb-4">
        <h2 className="text-xl font-bold text-teal-800">Implementation Checklist Configuration</h2>
        <p className="text-sm text-slate-600 mt-1">
          Configure which sections are visible and enable bullet point formatting for list fields.
        </p>
      </div>

      {/* Section Visibility */}
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
          <span className="w-6 h-6 bg-teal-100 text-teal-700 rounded-full flex items-center justify-center text-xs font-bold">1</span>
          Section Visibility
        </h3>
        <p className="text-xs text-slate-500 mb-4">Toggle which sections appear in the Implementation Checklist form.</p>
        
        <div className="grid grid-cols-2 gap-4">
          {Object.entries(sections).map(([key, section]) => (
            <label key={key} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-100 transition-colors">
              <input
                type="checkbox"
                checked={section.enabled !== false}
                onChange={(e) => updateSection(key, { enabled: e.target.checked })}
                className="w-5 h-5 text-teal-600 rounded"
              />
              <div className="flex-1">
                <span className="font-medium text-sm text-slate-800">{section.title || key}</span>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Bullet Point Formatting */}
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
          <span className="w-6 h-6 bg-teal-100 text-teal-700 rounded-full flex items-center justify-center text-xs font-bold">2</span>
          Bullet Point Formatting
        </h3>
        <p className="text-xs text-slate-500 mb-4">
          Enable bullet points for text fields. When enabled, each line entered by the user will display as a bullet point in both the preview and PDF.
        </p>
        
        <div className="grid grid-cols-2 gap-4">
          {Object.entries(bulletFormatting).map(([key, field]) => (
            <label key={key} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-100 transition-colors">
              <input
                type="checkbox"
                checked={field.enabled === true}
                onChange={(e) => updateBulletFormat(key, e.target.checked)}
                className="w-5 h-5 text-teal-600 rounded"
              />
              <div className="flex-1">
                <span className="font-medium text-sm text-slate-800">{field.label || key}</span>
                {field.enabled && (
                  <span className="ml-2 text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded">Bullets ON</span>
                )}
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Special Requests Prompts */}
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
          <span className="w-6 h-6 bg-teal-100 text-teal-700 rounded-full flex items-center justify-center text-xs font-bold">3</span>
          Special Requests Prompts
        </h3>
        <p className="text-xs text-slate-500 mb-4">
          These prompts appear as guidance bullets in the Special Requests section to remind users what information to include.
        </p>
        
        <div className="space-y-2">
          {specialRequestsPrompts.map((prompt, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <span className="text-teal-600 text-lg">•</span>
              <input
                type="text"
                value={prompt}
                onChange={(e) => {
                  const updated = [...specialRequestsPrompts];
                  updated[idx] = e.target.value;
                  updatePrompts(updated);
                }}
                className="input-field flex-1 text-sm"
                placeholder="Enter prompt..."
              />
              <button
                onClick={() => {
                  const updated = specialRequestsPrompts.filter((_, i) => i !== idx);
                  updatePrompts(updated);
                }}
                className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button
            onClick={() => updatePrompts([...specialRequestsPrompts, ''])}
            className="flex items-center gap-1 text-sm text-teal-600 hover:text-teal-800 font-medium mt-2"
          >
            <Plus className="w-4 h-4" /> Add Prompt
          </button>
        </div>
      </div>

      {/* User Account Roles */}
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
          <span className="w-6 h-6 bg-teal-100 text-teal-700 rounded-full flex items-center justify-center text-xs font-bold">4</span>
          User Account Roles
        </h3>
        <p className="text-xs text-slate-500 mb-4">
          Define the available roles for user accounts in the User Accounts section.
        </p>
        
        <div className="flex flex-wrap gap-2 mb-4">
          {userAccountRoles.map((role, idx) => (
            <div key={idx} className="flex items-center gap-1 bg-slate-100 px-3 py-1.5 rounded-full">
              <input
                type="text"
                value={role}
                onChange={(e) => {
                  const updated = [...userAccountRoles];
                  updated[idx] = e.target.value;
                  updateRoles(updated);
                }}
                className="bg-transparent border-none text-sm font-medium text-slate-700 w-24 focus:outline-none"
              />
              <button
                onClick={() => {
                  const updated = userAccountRoles.filter((_, i) => i !== idx);
                  updateRoles(updated);
                }}
                className="text-red-400 hover:text-red-600"
              >
                ×
              </button>
            </div>
          ))}
        </div>
        <button
          onClick={() => updateRoles([...userAccountRoles, 'New Role'])}
          className="flex items-center gap-1 text-sm text-teal-600 hover:text-teal-800 font-medium"
        >
          <Plus className="w-4 h-4" /> Add Role
        </button>
      </div>

      {/* Deposit Frequency Options */}
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
          <span className="w-6 h-6 bg-teal-100 text-teal-700 rounded-full flex items-center justify-center text-xs font-bold">5</span>
          Deposit Frequency Options
        </h3>
        <p className="text-xs text-slate-500 mb-4">
          Define the available deposit frequency options in the Billing & Deposit Structure section.
        </p>
        
        <div className="flex flex-wrap gap-2 mb-4">
          {depositFrequencyOptions.map((option, idx) => (
            <div key={idx} className="flex items-center gap-1 bg-slate-100 px-3 py-1.5 rounded-full">
              <input
                type="text"
                value={option}
                onChange={(e) => {
                  const updated = [...depositFrequencyOptions];
                  updated[idx] = e.target.value;
                  updateDepositOptions(updated);
                }}
                className="bg-transparent border-none text-sm font-medium text-slate-700 w-28 focus:outline-none"
              />
              <button
                onClick={() => {
                  const updated = depositFrequencyOptions.filter((_, i) => i !== idx);
                  updateDepositOptions(updated);
                }}
                className="text-red-400 hover:text-red-600"
              >
                ×
              </button>
            </div>
          ))}
        </div>
        <button
          onClick={() => updateDepositOptions([...depositFrequencyOptions, 'New Option'])}
          className="flex items-center gap-1 text-sm text-teal-600 hover:text-teal-800 font-medium"
        >
          <Plus className="w-4 h-4" /> Add Option
        </button>
      </div>

      {/* Preview Note */}
      <div className="bg-teal-50 p-4 rounded border border-teal-200">
        <p className="text-xs text-teal-900 leading-relaxed">
          <strong>Note:</strong> Changes here will affect all new agreements. Section visibility toggles control what users see when filling out the Implementation Checklist. Bullet formatting options determine how multi-line text fields render in the form preview and exported PDF.
        </p>
      </div>
    </div>
  );
}

// Editable Field Component
function EditableField({ value, onSave, className, placeholder, isEditing, onEdit, onClose, multiline, inline }) {
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleSave = () => {
    onSave(localValue);
    onClose();
  };

  if (isEditing) {
    return (
      <div className={inline ? 'inline-flex items-center gap-2' : 'space-y-2'}>
        {multiline ? (
          <textarea
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            className="input-field w-full"
            rows={4}
            autoFocus
          />
        ) : (
          <input
            type="text"
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            className="input-field"
            autoFocus
          />
        )}
        <div className="flex gap-2">
          <button
            onClick={handleSave}
            className="px-3 py-1 bg-emerald-600 text-white rounded text-sm hover:bg-emerald-700"
          >
            Save
          </button>
          <button
            onClick={onClose}
            className="px-3 py-1 bg-slate-200 text-slate-700 rounded text-sm hover:bg-slate-300"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={onEdit}
      className={`${className} cursor-pointer hover:bg-yellow-50 hover:outline hover:outline-2 hover:outline-yellow-400 rounded px-2 py-1 transition-all group relative`}
    >
      {value || <span className="text-slate-400 italic">{placeholder}</span>}
      <Edit2 className="w-3 h-3 absolute -right-1 -top-1 opacity-0 group-hover:opacity-100 text-yellow-600" />
    </div>
  );
}

// Default Template Structure (matching PDF)
function getDefaultTemplate() {
  return {
    header: {
      logo: 'GOVERNMENT WINDOW',
      title: 'No Term, Non-Exclusive SaaS Agreement'
    },
    principal: {
      name: 'Government Window, LLC',
      address: '125 Townpark Drive NW. Suite 500, Kennesaw GA 30144'
    },
    grant: {
      statement: 'This agreement sets out the terms upon which the Principal grants the use of certain software applications referred to as the Licensed Items, by way of Software as a Service ("SaaS"), to the End-User listed below.'
    },
    endUser: {
      licensee: { placeholder: 'Will pull from proposal.client_name' },
      agency: { placeholder: 'Will pull from proposal.agency_name' },
      contact: { placeholder: 'Will pull from proposal.contact_name' },
      phone: { placeholder: 'Will pull from proposal.contact_phone' },
      address: { placeholder: 'Will pull from proposal.client_address' }
    },
    scheduleA: {
      intro: 'Schedule "A" lists the service fees for each Module ("Licensed Items") selected below. The End-User will not be charged service fees until they launch the respective Module.',
      functionInstruction: 'In the "Function" column, check the box(es) for the Module(s) you will be launching. [W]=Webpay, [P]=Point of Sale, [I]=IVR',
      modules: ['Taxes', 'DMV', 'Court', 'Licenses', 'Permits', 'Utilities', 'Parks & Rec', 'eStore', 'Donations'],
      columns: [
        { id: 'module', label: 'Module', dbField: 'service_name', type: 'text', visible: true },
        { id: 'function', label: 'Function', dbField: 'function_codes', type: 'checkbox', visible: true },
        { id: 'reader_type', label: 'Reader Type', dbField: 'scenario_info.readerType', type: 'text', visible: true },
        { id: 'reader_qty', label: 'Reader Qty', dbField: 'scenario_info.readersQty', type: 'number', visible: true },
        { id: 'cc_rate', label: 'CC Rate', dbField: 'proposed_pricing.paymentRates.CC.takeRate', type: 'percentage', visible: true },
        { id: 'cc_min', label: 'CC Min', dbField: 'proposed_pricing.paymentRates.CC.minFee', type: 'currency', visible: true },
        { id: 'amex_rate', label: 'AmEx Rate', dbField: 'proposed_pricing.paymentRates.AMEX.takeRate', type: 'percentage', visible: true },
        { id: 'amex_min', label: 'AmEx Min', dbField: 'proposed_pricing.paymentRates.AMEX.minFee', type: 'currency', visible: true },
        { id: 'echeck', label: 'E-check', dbField: 'proposed_pricing.paymentRates.ACH.minFee', type: 'currency', visible: true }
      ]
    },
    termsAndConditions: {
      content: ''
    },
    stipulations: {
      costAllocation: 'Cost allocation fees based on a percentage of each transaction amount and/or a minimum fee per transaction, as designated in Schedule "A". Service fees will be determined by the client\'s merchant service provider.'
    },
    legal: {
      includeDefinitions: true,
      includeLicense: true,
      includeTermination: true,
      includeWarranty: true,
      governingLaw: 'State of Georgia'
    },
    achAgreement: {
      grantStatement: '',
      authorizationText: '',
      termsAndConditionsText: ''
    },
    crofAgreement: {
      termsOfSupply: [
        { 
          heading: '1. Equipment Ownership and Responsibility', 
          body: 'This Terms of Supply ("Terms") applies to the card reader(s) as depicted on the front of this order ("Equipment") provided by Government Window ("GW") to you, the customer whose name appears on the front hereof ("Customer"). The Equipment remains the property of GW at all times.' 
        },
        { 
          heading: '2. Use of Equipment', 
          body: 'The Equipment is provided solely for use with GW\'s credit card processing services. The customer agrees to use the Equipment in accordance with all applicable laws, regulations, and instructions provided by GW. The customer is responsible for the safekeeping and proper use of the Equipment.' 
        },
        { 
          heading: '3. Return of Equipment', 
          body: 'The customer agrees to return the Equipment to GW upon termination of the credit card processing agreement, regardless of the reason for termination. The Equipment must be returned in good working order and with all accessories (if applicable).' 
        },
        { 
          heading: '4. Failure to Return Equipment', 
          body: 'In the event that Customer fails to return the Equipment within thirty (30) business days of termination, Customer will be charged a pro-rated portion of the replacement cost of each missing unit. The replacement cost will be determined by GW in its sole discretion but shall not exceed the value as depicted on the face hereof.' 
        },
        { 
          heading: '5. Customer Acknowledgement', 
          body: 'By signing below, Customer acknowledges that they have read and understood these Terms and agree to the following:' 
        }
      ],
      acknowledgementBullets: [
        'The Equipment remains the property of GW.',
        'The customer is responsible for the safekeeping and proper use of the Equipment.',
        'The customer agrees to return the Equipment to GW upon termination of the credit card processing agreement.',
        'The customer agrees to be charged a pro-rated portion of the replacement cost for any missing Equipment.'
      ]
    },
    implementationChecklist: {
      // Section visibility toggles
      sections: {
        customerInfo: { enabled: true, title: 'Customer Information' },
        goLiveDate: { enabled: true, title: 'Proposed Go-Live Date' },
        servicesRates: { enabled: true, title: 'Services & Proposed Rates' },
        cardReaderOrder: { enabled: true, title: 'Card Reader Order' },
        requiredDocuments: { enabled: true, title: 'Required Documents' },
        specialRequests: { enabled: true, title: 'Special Requests / Notes' },
        itContact: { enabled: true, title: 'IT & Technical Contact Information' },
        billingDeposit: { enabled: true, title: 'Billing & Deposit Structure' },
        permissions: { enabled: true, title: 'Permissions & Operations' },
        userAccounts: { enabled: true, title: 'User Accounts' },
        brandingAssets: { enabled: true, title: 'Branding Assets' },
        trainingOnboarding: { enabled: true, title: 'Training & Onboarding' }
      },
      // Bullet point formatting options for text fields
      bulletFormatting: {
        integrationNotes: { enabled: false, label: 'Integration Requirements' },
        estoreCategories: { enabled: false, label: 'eStore Categories' },
        specialRequests: { enabled: true, label: 'Special Requests' },
        depositStructure: { enabled: false, label: 'Deposit Structure' },
        midSetupNotes: { enabled: false, label: 'MID Setup Notes' },
        dailyReports: { enabled: true, label: 'Daily Reports Needed' },
        notifications: { enabled: true, label: 'Notifications & Alerts' },
        userAccountsNotes: { enabled: false, label: 'User Account Notes' },
        logoNotes: { enabled: false, label: 'Logo Notes' },
        headerImageNotes: { enabled: false, label: 'Header Image Notes' },
        brandingNotes: { enabled: false, label: 'Branding Notes' },
        trainingNotes: { enabled: false, label: 'Training Notes' }
      },
      // Special requests prompts (shown as guidance bullets)
      specialRequestsPrompts: [
        'Tax commissioner site',
        'Invoicing system',
        'Citizen portal / autopay',
        'Text-to-pay / e-alerts',
        'Anything outside the "base package"'
      ],
      // User account role options
      userAccountRoles: ['Admin', 'Manager', 'Clerk', 'View Only'],
      // Deposit frequency options
      depositFrequencyOptions: ['Daily', 'Weekly', 'Bi-Weekly', 'Monthly', 'Custom Schedule']
    },
    footer: {
      copyright: '© 2025'
    }
  };
}
